import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Pill, Search, ShieldCheck, AlertTriangle, X, Send, Info, Loader2,
} from "lucide-react";

/**
 * Provider-only modal for prescribing a nephrology-approved medication from
 * the curated formulary. Runs a live eGFR safety check as soon as the doctor
 * picks a medication, and blocks (or asks for explicit override) if the drug
 * hits an `avoid_below_egfr` rule for this patient.
 *
 * Props:
 *   open          - controls visibility
 *   patientId     - the patient this prescription is FOR (required)
 *   patientName   - display name for the modal header
 *   onClose()     - called when modal closes (with or without success)
 *   onPrescribed(m) - called with the newly-created MedicationEntry after success
 */
export default function PrescribeMedicationModal({
  open, onClose, patientId, patientName, onPrescribed,
}) {
  const [formulary, setFormulary] = useState([]);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);   // formulary entry
  const [safety, setSafety] = useState(null);        // { level, reasons, egfr_used, typical_doses }
  const [dose, setDose] = useState("");
  const [frequency, setFrequency] = useState("Daily");
  const [supplyDays, setSupplyDays] = useState(30);
  const [override, setOverride] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [loadingSafety, setLoadingSafety] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Load formulary once
  useEffect(() => {
    if (!open) return;
    api.get("/formulary").then(({ data }) => setFormulary(data)).catch(() => {});
  }, [open]);

  // Reset internal state on close
  useEffect(() => {
    if (!open) {
      setQuery(""); setSelected(null); setSafety(null);
      setDose(""); setFrequency("Daily"); setSupplyDays(30);
      setOverride(false); setOverrideReason("");
    }
  }, [open]);

  // Autocomplete-friendly filtered list — group by category so ARBs / ACEi cluster
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const out = formulary.filter((f) =>
      !q ? true : f.generic_name.toLowerCase().includes(q)
        || f.category.toLowerCase().includes(q)
        || f.primary_indication.toLowerCase().includes(q)
    );
    // Group by category
    const grouped = {};
    for (const f of out) (grouped[f.category] ||= []).push(f);
    return grouped;
  }, [formulary, query]);

  // Live safety evaluation on selection change
  const selectDrug = async (entry) => {
    setSelected(entry); setSafety(null);
    setDose((entry.typical_doses && entry.typical_doses[0]) || "");
    if (entry.avoid) {
      setSafety({
        level: "avoid",
        reasons: [(entry.safety_rule && entry.safety_rule.watch) || "Nephrotoxic — not for CKD prescribing."],
        egfr_used: null,
      });
      return;
    }
    setLoadingSafety(true);
    try {
      const { data } = await api.post("/formulary/check", { generic_name: entry.generic_name }, {
        params: { patient_id: patientId },
      });
      setSafety(data);
    } catch (err) {
      toast.error("Could not evaluate safety — please retry.");
    } finally {
      setLoadingSafety(false);
    }
  };

  const submit = async () => {
    if (!selected || !dose.trim()) {
      toast.error("Select a medication and enter a dose");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/provider/prescribe", {
        generic_name: selected.generic_name,
        dose: dose.trim(),
        frequency: frequency.trim() || null,
        refill_supply_days: parseInt(supplyDays, 10) || 30,
        override_safety: override,
        override_reason: override ? overrideReason.trim() : null,
      }, { params: { patient_id: patientId } });
      toast.success(`Prescribed ${data.medication.name} ${data.medication.dose || ""}`);
      onPrescribed && onPrescribed(data.medication);
      onClose && onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.safety) {
        setSafety(detail.safety);
        toast.error(detail.message || "Blocked by safety rule");
      } else {
        toast.error(typeof detail === "string" ? detail : "Prescription failed");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const levelStyles = {
    safe:    { c: "#4ADE80", bg: "rgba(74,222,128,0.10)",  brd: "rgba(74,222,128,0.35)", label: "Safe for this patient" },
    caution: { c: "#FBBF24", bg: "rgba(251,191,36,0.10)",  brd: "rgba(251,191,36,0.40)", label: "Caution — see notes" },
    avoid:   { c: "#F87171", bg: "rgba(248,113,113,0.10)", brd: "rgba(248,113,113,0.50)", label: "Avoid at this eGFR" },
  };
  const sStyle = safety && (levelStyles[safety.level] || levelStyles.safe);
  const blocked = safety?.level === "avoid" && !override;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
         data-testid="prescribe-modal">
      <motion.div initial={{ opacity: 0, y: 12, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
                  className="ros-card max-w-3xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between border-b border-[#272E2B] pb-4 mb-5">
          <div>
            <div className="ros-label flex items-center gap-2">
              <Pill size={12} /> Prescribe · Nephrology Formulary
            </div>
            <div className="font-heading text-xl text-white mt-1">{patientName || "Patient"}</div>
            <div className="text-xs text-[#A3ACA7] mt-1">
              All prescriptions are audit-logged with your name and timestamp. Patient sees them instantly.
            </div>
          </div>
          <button onClick={onClose} className="text-[#A3ACA7] hover:text-white p-1"
                  data-testid="prescribe-close">
            <X size={20} />
          </button>
        </div>

        {/* Content — 2 columns */}
        <div className="grid md:grid-cols-2 gap-5 overflow-y-auto pr-1">
          {/* Left: searchable formulary */}
          <div>
            <div className="relative mb-3">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3ACA7]" />
              <input
                type="text" placeholder="Search: ACE, SGLT2, potassium binder…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="prescribe-search"
                className="w-full bg-[#1C201E] border border-[#272E2B] rounded-lg pl-9 pr-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#60A5FA]"
              />
            </div>
            <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1" data-testid="prescribe-list">
              {Object.entries(filtered).map(([cat, items]) => (
                <div key={cat}>
                  <div className="text-[10px] uppercase tracking-widest text-[#7A8480] font-semibold px-1 mt-3 mb-1">
                    {cat}
                  </div>
                  {items.map((f) => {
                    const active = selected?.generic_name === f.generic_name;
                    const avoidEntry = !!f.avoid;
                    return (
                      <button
                        key={f.generic_name}
                        type="button"
                        onClick={() => selectDrug(f)}
                        data-testid={`prescribe-option-${f.generic_name.replace(/\s+/g, "-")}`}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm border transition-colors ${
                          active
                            ? "bg-[#60A5FA]/15 border-[#60A5FA]/60 text-white"
                            : "bg-[#1C201E] border-[#272E2B] hover:border-[#60A5FA]/40 text-white/90"
                        } ${avoidEntry ? "opacity-60" : ""}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{f.generic_name}</span>
                          {avoidEntry && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#F87171]/15 text-[#F87171] border border-[#F87171]/30">AVOID</span>}
                        </div>
                        <div className="text-[11px] text-[#A3ACA7] mt-0.5 truncate">{f.primary_indication}</div>
                      </button>
                    );
                  })}
                </div>
              ))}
              {Object.keys(filtered).length === 0 && (
                <div className="text-xs text-[#A3ACA7] py-8 text-center">No matches — try broader keywords.</div>
              )}
            </div>
          </div>

          {/* Right: selection detail + safety + dose */}
          <div>
            {!selected ? (
              <div className="h-full flex flex-col items-center justify-center text-center text-[#A3ACA7] p-10">
                <Pill size={26} className="mb-3 text-[#60A5FA]" />
                <div className="text-sm">Choose a medication from the list to see the safety check for this patient.</div>
              </div>
            ) : (
              <AnimatePresence mode="wait">
                <motion.div key={selected.generic_name}
                            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
                            data-testid="prescribe-detail">
                  <div className="font-heading text-lg text-white">{selected.generic_name}</div>
                  <div className="text-xs text-[#A3ACA7] mt-0.5">{selected.category}</div>
                  <div className="text-sm text-white/90 mt-3">{selected.primary_indication}</div>

                  {/* Live safety verdict */}
                  <div className="mt-4">
                    {loadingSafety ? (
                      <div className="flex items-center gap-2 text-xs text-[#A3ACA7]">
                        <Loader2 size={14} className="animate-spin" /> Evaluating against patient eGFR…
                      </div>
                    ) : safety && sStyle ? (
                      <div className="rounded-xl p-4 border"
                           style={{ background: sStyle.bg, borderColor: sStyle.brd }}
                           data-testid={`prescribe-safety-${safety.level}`}>
                        <div className="flex items-center gap-2">
                          {safety.level === "avoid" ? <AlertTriangle size={16} style={{ color: sStyle.c }} />
                            : safety.level === "caution" ? <AlertTriangle size={16} style={{ color: sStyle.c }} />
                            : <ShieldCheck size={16} style={{ color: sStyle.c }} />}
                          <div className="text-sm font-semibold" style={{ color: sStyle.c }}>
                            {sStyle.label}
                          </div>
                        </div>
                        {safety.egfr_used != null && (
                          <div className="text-[11px] text-[#A3ACA7] mt-1">
                            Patient eGFR: <span className="text-white font-semibold">{safety.egfr_used.toFixed?.(0) ?? safety.egfr_used} mL/min/1.73m²</span>
                          </div>
                        )}
                        <ul className="mt-2 space-y-1">
                          {(safety.reasons || []).map((r, i) => (
                            <li key={i} className="text-xs text-white/85 leading-snug flex items-start gap-1.5">
                              <Info size={11} className="mt-0.5 shrink-0 opacity-70" />
                              <span>{r}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </div>

                  {/* Dose picker */}
                  {!selected.avoid && (
                    <>
                      <div className="mt-4">
                        <div className="ros-label">Dose</div>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {(selected.typical_doses || []).map((d) => (
                            <button key={d} type="button" onClick={() => setDose(d)}
                                    data-testid={`prescribe-dose-${d.replace(/\s+/g, "-")}`}
                                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                                      dose === d
                                        ? "bg-[#60A5FA] text-black border-[#60A5FA]"
                                        : "bg-[#1C201E] text-[#A3ACA7] border-[#272E2B] hover:text-white hover:border-[#60A5FA]/40"
                                    }`}>
                              {d}
                            </button>
                          ))}
                        </div>
                        <input type="text" value={dose} onChange={(e) => setDose(e.target.value)}
                               placeholder="or type a custom dose (e.g. 12.5 mg BID)"
                               data-testid="prescribe-dose-input"
                               className="w-full mt-3 bg-[#1C201E] border border-[#272E2B] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#60A5FA]" />
                      </div>

                      <div className="mt-3">
                        <div className="ros-label">Frequency</div>
                        <input type="text" value={frequency} onChange={(e) => setFrequency(e.target.value)}
                               data-testid="prescribe-frequency"
                               className="w-full mt-2 bg-[#1C201E] border border-[#272E2B] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#60A5FA]" />
                      </div>

                      <div className="mt-3">
                        <div className="ros-label">Supply per refill (days)</div>
                        <div className="mt-2 flex items-center gap-2">
                          {[30, 60, 90].map((d) => (
                            <button key={d} type="button" onClick={() => setSupplyDays(d)}
                                    data-testid={`prescribe-supply-${d}`}
                                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                                      supplyDays === d
                                        ? "bg-[#60A5FA] text-black border-[#60A5FA]"
                                        : "bg-[#1C201E] text-[#A3ACA7] border-[#272E2B] hover:text-white hover:border-[#60A5FA]/40"
                                    }`}>
                              {d} days
                            </button>
                          ))}
                          <input type="number" min="7" max="180"
                                 value={supplyDays}
                                 onChange={(e) => setSupplyDays(e.target.value)}
                                 data-testid="prescribe-supply-input"
                                 className="w-20 bg-[#1C201E] border border-[#272E2B] rounded-lg px-2 py-1 text-sm text-white text-center focus:outline-none focus:border-[#60A5FA]" />
                          <span className="text-[11px] text-[#A3ACA7]">custom</span>
                        </div>
                        <div className="text-[10px] text-[#A3ACA7] mt-1">
                          Med Nexus will remind the patient 3 days before the refill runs out.
                        </div>
                      </div>

                      {/* Override option for AVOID-level safety */}
                      {safety?.level === "avoid" && !selected.avoid && (
                        <div className="mt-4 rounded-lg border border-[#F87171]/40 bg-[#F87171]/5 p-3">
                          <label className="flex items-start gap-2 text-xs text-white cursor-pointer">
                            <input type="checkbox" checked={override}
                                   onChange={(e) => setOverride(e.target.checked)}
                                   data-testid="prescribe-override-toggle"
                                   className="mt-0.5 accent-[#F87171]" />
                            <span>
                              I acknowledge the safety warning and take clinical responsibility for prescribing anyway.
                            </span>
                          </label>
                          {override && (
                            <textarea value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)}
                                      placeholder="Required: document the clinical rationale (e.g. 'no better option; ICU-level monitoring in place')"
                                      data-testid="prescribe-override-reason"
                                      rows={2}
                                      className="w-full mt-2 bg-[#1C201E] border border-[#272E2B] rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-[#F87171]" />
                          )}
                        </div>
                      )}
                    </>
                  )}
                </motion.div>
              </AnimatePresence>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-[#272E2B] mt-5 pt-4 flex items-center justify-between gap-3">
          <div className="text-[11px] text-[#A3ACA7]">
            Signed by <span className="text-white font-semibold">the prescribing provider</span> · auto-recorded in Audit Trail.
          </div>
          <div className="flex gap-2">
            <button onClick={onClose}
                    className="px-4 py-2 rounded-full text-sm text-[#A3ACA7] hover:text-white hover:bg-[#1C201E]">
              Cancel
            </button>
            <button
              onClick={submit}
              disabled={!selected || selected.avoid || !dose.trim() || submitting || blocked || (override && !overrideReason.trim())}
              data-testid="prescribe-submit"
              className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#60A5FA] text-black font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#60A5FA]/90"
            >
              <Send size={14} />
              {submitting ? "Prescribing…" : "Prescribe"}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
